import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { HttpOperation } from '../../enums/http-operation';
import { TagModel } from '../../models/tag/tag.model';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromTagsActions from './tags.actions';

@Injectable()
export class TagsEffects {
  fetchTags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTagsActions.fetchTagList),
      switchMap(() => {
        const endpoint = this.endpointService.getTagSlug(HttpOperation.Get);

        return this.http.get<unknown[]>(endpoint).pipe(
          map((json: unknown[]) => {
            const tags = json.map((plain) => plainToInstance(TagModel, plain));
            return fromTagsActions.setTagList({ payload: tags });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Tags',
              fromTagsActions.setTagList,
              fromTagsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  createTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTagsActions.createTag),
      map((action) => action.payload),
      mergeMap((tagModel) => {
        const endpoint = this.endpointService.getTagSlug(HttpOperation.Post);

        const httpTagModel = instanceToPlain(tagModel as TagModel);
        delete (httpTagModel as any).id;

        return this.http.post<unknown>(endpoint, httpTagModel).pipe(
          map((json: unknown) => {
            const created = plainToInstance(TagModel, json);
            return fromTagsActions.tagCreated(created);
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Tags',
              fromTagsActions.createTag,
              fromTagsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTagsActions.updateTag),
      map((action) => action.payload),
      mergeMap((tagModel) => {
        const endpoint = this.endpointService.getTagSlug(HttpOperation.Put, tagModel.id);
        const httpTagModel = instanceToPlain(tagModel as TagModel);

        return this.http.put<TagModel>(endpoint, httpTagModel).pipe(
          map((json: unknown) => {
            const updated = plainToInstance(TagModel, json);
            return fromTagsActions.tagUpdated(updated);
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Tags',
              fromTagsActions.createTag,
              fromTagsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTagsActions.deleteTag),
      map((action) => action.payload),
      mergeMap((tagModelOrArray) => {
        let requests: Observable<ArrayBuffer>[];
        let endpoint: string;

        if (Array.isArray(tagModelOrArray)) {
          requests = tagModelOrArray.map((model) => {
            endpoint = this.endpointService.getTagSlug(HttpOperation.Delete, model.id);
            return this.http.delete(endpoint, model.id);
          });
        } else {
          endpoint = this.endpointService.getTagSlug(HttpOperation.Delete, tagModelOrArray.id);
          requests = [this.http.delete(endpoint, tagModelOrArray.id)];
        }

        return forkJoin(requests).pipe(
          map(() => {
            return fromTagsActions.tagDeleted({ payload: tagModelOrArray });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Tags',
              fromTagsActions.deleteTag,
              fromTagsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
